"use client";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { FormikInput, FormikSelect, Switch } from "../input/inputs";
import { scene_types } from "@/data/shot-data";
import { Info, Music, Zap } from "lucide-react";

const ProductionSetupModal = ({
  initializeProduction,
  initialValues,
}: {
  initializeProduction: (
    count: number,
    type: string,
    dance_choreographer_required?: boolean,
    action_sequence_required?: boolean,
  ) => Promise<any> | any;
  initialValues?: {
    numCameras: number;
    scene_type: string;
    dance_choreographer_required?: boolean;
    action_sequence_required?: boolean;
  };
}) => {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <div className="w-full max-w-lg">
        <div className="my-8 text-center">
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
            Production Setup for this Scene
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Configure your scene and camera setup to get started
          </p>
        </div>

        <div className="p-6 md:p-8">
          <Formik
            initialValues={
              initialValues || {
                numCameras: 1,
                scene_type: "",
                dance_choreographer_required: false,
                action_sequence_required: false,
              }
            }
            validationSchema={Yup.object({
              numCameras: Yup.number()
                .min(1, "Must be at least 1")
                .max(20, "Cannot add more than 20 cameras")
                .required("Number of cameras is required"),
              scene_type: Yup.string().required("Scene type is required"),
            })}
            onSubmit={async (values) => {
              await initializeProduction(
                values.numCameras,
                values.scene_type,
                values.dance_choreographer_required,
                values.action_sequence_required,
              );
            }}
          >
            {({ isSubmitting, setFieldValue, values }) => (
              <Form className="flex flex-col gap-6">
                <div className="grid gap-5">
                  <FormikSelect
                    label="Scene Type"
                    name="scene_type"
                    selectedOption="Select scene type"
                    optionData={scene_types}
                    enableRedAsterick
                    onChange={(e) =>
                      setFieldValue("scene_type", e.target.value)
                    }
                  />

                  <FormikInput
                    label="Number of Cameras"
                    name="numCameras"
                    type="number"
                    min={1}
                    max={20}
                    enableRedAsterick
                  />

                  <div className="flex flex-col gap-4">
                    <div
                      className={`relative p-5 rounded-3xl border transition-all duration-300 flex flex-col gap-4 group cursor-pointer ${
                        values.dance_choreographer_required
                          ? "bg-blumine-50/30 border-blumine-200 shadow-sm"
                          : "bg-slate-50/50 border-slate-100 hover:border-slate-200"
                      }`}
                      onClick={() =>
                        setFieldValue(
                          "dance_choreographer_required",
                          !values.dance_choreographer_required,
                        )
                      }
                    >
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <p className="text-[15px] font-bold text-slate-800">
                            Dance Sequence
                          </p>
                          <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                            Choreography needed
                          </p>
                        </div>
                        <Switch
                          isOn={values.dance_choreographer_required || false}
                          handleToggle={() =>
                            setFieldValue(
                              "dance_choreographer_required",
                              !values.dance_choreographer_required,
                            )
                          }
                          activeColor="bg-blumine-600"
                          divClasses="w-fit!"
                        />
                      </div>
                    </div>

                    <div
                      className={`relative p-5 rounded-3xl border transition-all duration-300 flex flex-col gap-4 group cursor-pointer ${
                        values.action_sequence_required
                          ? "bg-rose-50/30 border-rose-200 shadow-sm"
                          : "bg-slate-50/50 border-slate-100 hover:border-slate-200"
                      }`}
                      onClick={() =>
                        setFieldValue(
                          "action_sequence_required",
                          !values.action_sequence_required,
                        )
                      }
                    >
                      <div className="flex items-center justify-between w-full">
                        <div>
                          <p className="text-[15px] font-bold text-slate-800">
                            Action Sequence
                          </p>
                          <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                            Stunt & combat logic
                          </p>
                        </div>
                        <Switch
                          isOn={values.action_sequence_required || false}
                          handleToggle={() =>
                            setFieldValue(
                              "action_sequence_required",
                              !values.action_sequence_required,
                            )
                          }
                          activeColor="bg-rose-500"
                          divClasses="w-fit!"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 bg-slate-50 border border-slate-100 p-4 rounded-xl">
                  <Info className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-[13px] text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-900">
                      Important:
                    </span>{" "}
                    These settings are permanent. Once the production is
                    initialized for this scene, you won't be able to change the
                    scene type or camera count.
                  </p>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-11 rounded-3xl bg-blumine-600 text-white text-sm md:text-base font-medium shadow-sm transition-all duration-300 hover:bg-blumine-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blumine-600/40 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Initializing..." : "Complete Setup"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ProductionSetupModal;
